import { StrictMode, Component, ErrorInfo } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import { ThemeProvider } from '@/components/common/ThemeProvider';
import { AuthProvider } from '@/contexts/AuthContext';
import { App } from './App';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

class ErrorBoundaryWrapper extends Component<{children: React.ReactNode}, {errorMsg: string | null}> {
  constructor(props: any) {
    super(props);
    this.state = { errorMsg: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { errorMsg: error.stack || error.message };
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {}
  render() {
    if (this.state.errorMsg) {
      return <div style={{ color: 'red', background: 'white', padding: 20, zIndex: 99999, position: 'absolute' }}><pre>GLOBAL ERROR: {this.state.errorMsg}</pre></div>;
    }
    return this.props.children;
  }
}

const rootElement = document.getElementById('root');
const app = (
  <StrictMode>
    <ErrorBoundaryWrapper>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
            <AuthProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </AuthProvider>
          </ThemeProvider>
          {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundaryWrapper>
  </StrictMode>
);

if (rootElement?.hasChildNodes()) {
  hydrateRoot(rootElement, app);
} else if (rootElement) {
  createRoot(rootElement).render(app);
}
